import React, { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    HomePageHeader,
    HomePageFilters,
    HomePageContent,
    useHomePage
} from '@/features/tasks/components/HomePage/index.js';
import { GlobalContext } from '@/app/providers/globalState';
import ProjectCreatedBar from '@/features/tasks/components/HomePage/ProjectCreatedBar.jsx';
import { EditProjectModal } from '@/features/tasks/components/HomePage/EditProjectModal.jsx';
import { DuplicateTaskModal } from '@/features/tasks/components/HomePage/DuplicateTaskModal.jsx';
import { EditTaskModal } from '@/features/tasks/components/HomePage/EditTaskModal.jsx';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import ModelProvidersOnboardingModal from '@/features/settings/components/ModelProvidersOnboardingModal.jsx';


export const HomePage = ({ moveToScreen }) => {
    const navigate = useNavigate();
    const { refreshUsecases, setCurrentUsecaseId } = useContext(GlobalContext);
    const {
        // State
        viewType,
        selectedProject,
        filterValue,
        searchValue,
        taskType,
        agenticFilter,
        userFilter,
        statusFilter,
        allUsers,
        filteredApps,
        allUsecases,
        projectCreatedBar,
        isEditProjectModalOpen,
        currentProjectData,
        editProjectLoading,
        isDuplicateTaskModalOpen,
        taskToDuplicate,
        isEditTaskModalOpen,
        taskToEdit,
        editTaskMode,
        homeLoading,
        allApps,
        isProvidersOnboardingOpen,

        // Actions
        setViewType,
        setFilterValue,
        setSearchValue,
        setTaskType,
        setAgenticFilter,
        setUserFilter,
        setStatusFilter,
        openCreateProjectModal,
        openNewTaskModal,
        openProject,
        closeProject,
        closeProvidersOnboardingModal,
        continueFromProvidersOnboarding,
        closeEditProjectModal,
        closeDuplicateTaskModal,
        handleDeleteUsecase,
        handleDeleteProject,
        handleEditProject,
        handleSaveEditedProject,
        handleModelDeployment,
        handleDuplicateUsecase,
        handleCreateDuplicateTask,
        handleEditTask,
        handleUpdateTask,
        handleBulkDeleteUsecases,
        handleBulkMoveUsecases,
        closeEditTaskModal,
        closeProjectCreatedBar,
        handleViewProject,
    } = useHomePage();

    const handleStartTaskSteps = () => {
        if (!projectCreatedBar.usecaseId) {
            navigate('/');
            return;
        }
        const usecase = allUsecases.find((u) => u._id === projectCreatedBar.usecaseId);
        const url = usecase
            ? taskUrlForUsecase(usecase, { objective: usecase._id })
            : null;
        if (!url) {
            navigate('/');
            return;
        }
        setCurrentUsecaseId(usecase._id);
        navigate(url);
    };

    useEffect(() => {
        refreshUsecases();
    }, [refreshUsecases]);

    // First-time / no-projects empty state: Figma shows only nav + centered
    // empty card — no page title, subtitle, or filters.
    const isProjectsEmpty = (allApps || []).length === 0;
    // Projects exist but current filters/search yield nothing — keep header +
    // filters, and vertically center the empty card in the remaining space.
    const isFilteredEmpty = !isProjectsEmpty && (filteredApps || []).length === 0;
    const shouldFillViewport = isProjectsEmpty || isFilteredEmpty;

    // Fill the scrollport below the navbar (do not use 100vh — that is taller
    // than the available area and creates empty-state page scroll + pulls the
    // sticky nav off the top of the viewport).
    return (
        <div className="page-content-shell" style={{
            display: "flex",
            flexDirection: "column",
            // Grow with card content so bottom padding is after the last row
            // instead of being eaten by a viewport-locked flex item.
            flex: shouldFillViewport ? 1 : '1 0 auto',
            minHeight: '100%',
            ...(shouldFillViewport
                ? {
                    height: '100%',
                    ...(isProjectsEmpty ? { overflow: 'hidden' } : null),
                }
                : null),
            paddingTop: '0',
            background: 'var(--semantic-surface-main)',
        }}>
            {/* Notification bar below nav bar */}
            <ProjectCreatedBar
                visible={projectCreatedBar.visible}
                projectName={projectCreatedBar.projectName}
                taskName={projectCreatedBar.taskName}
                onClose={closeProjectCreatedBar}
                onStartTaskSteps={handleStartTaskSteps}
                onViewProject={() => handleViewProject(projectCreatedBar.projectName)}
                isProjectCreation={projectCreatedBar.isProjectCreation}
            />
            {!isProjectsEmpty ? (
                <>
                    <HomePageHeader onCreateProject={openCreateProjectModal} />

                    <HomePageFilters
                        allApps={allApps}
                        filterValue={filterValue}
                        setFilterValue={setFilterValue}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        taskType={taskType}
                        setTaskType={setTaskType}
                        agenticFilter={agenticFilter}
                        setAgenticFilter={setAgenticFilter}
                        userFilter={userFilter}
                        setUserFilter={setUserFilter}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        allUsers={allUsers}
                        viewType={viewType}
                        setViewType={setViewType}
                    />
                </>
            ) : null}

            <HomePageContent
                viewType={viewType}
                filteredApps={filteredApps}
                allApps={allApps}
                allUsecases={allUsecases}
                filterValue={filterValue}
                searchValue={searchValue}
                selectedProject={selectedProject}
                onOpenProject={openProject}
                onCloseProject={closeProject}
                onDeleteUsecase={handleDeleteUsecase}
                onBulkDeleteUsecases={handleBulkDeleteUsecases}
                onBulkMoveUsecases={handleBulkMoveUsecases}
                onDeleteProject={handleDeleteProject}
                onEditProject={handleEditProject}
                onModelDeployment={handleModelDeployment}
                onMoveToScreen={moveToScreen}
                onNewTask={openNewTaskModal}
                onDuplicateUsecase={handleDuplicateUsecase}
                onEditTask={handleEditTask}
                onCreateProject={openCreateProjectModal}
                isLoading={homeLoading}
                isFullPageEmpty={isProjectsEmpty}
            />

            <EditProjectModal
                isOpen={isEditProjectModalOpen}
                onClose={closeEditProjectModal}
                onSave={handleSaveEditedProject}
                projectData={currentProjectData}
                allUsecases={allUsecases}
                loading={editProjectLoading}
            />

            <DuplicateTaskModal
                isOpen={isDuplicateTaskModalOpen}
                onClose={closeDuplicateTaskModal}
                originalTask={taskToDuplicate}
                allProjects={allApps || []}
                onDuplicateTask={handleCreateDuplicateTask}
            />

            <EditTaskModal
                isOpen={isEditTaskModalOpen}
                onClose={closeEditTaskModal}
                projectName={taskToEdit?.group_name || ''}
                allProjects={allApps || []}
                task={taskToEdit}
                mode={editTaskMode}
                onUpdateTask={handleUpdateTask}
            />

            <ModelProvidersOnboardingModal
                open={isProvidersOnboardingOpen}
                onClose={closeProvidersOnboardingModal}
                onContinue={continueFromProvidersOnboarding}
            />
        </div>
    );
};